import { CUSTOM_DOMAIN_CACHE_KEY, CustomDomainOrg, getCachedCustomDomainOrg, isCustomDomain } from './domain';

/**
 * On a custom domain, make sure the hostname→org mapping is cached before the
 * Router renders (Router picks its whole tree off getOrgSlugFromHostname()).
 * Returns true when the app can proceed as an org domain.
 */
export const resolveCustomDomain = async (): Promise<boolean> => {
  if (!isCustomDomain()) return true;
  if (getCachedCustomDomainOrg()) {
    void refresh(); // refresh cache in the background, don't block boot
    return true;
  }
  return refresh();
};

// A transient network hiccup on the very first visit would otherwise brand a
// perfectly working domain as "not configured" — retry before giving up.
// A definitive 404 (unknown/disabled domain) is NOT retried.
const RESOLVE_ATTEMPTS = 3;

const refresh = async (): Promise<boolean> => {
  for (let attempt = 0; attempt < RESOLVE_ATTEMPTS; attempt++) {
    try {
      const url = new URL(import.meta.env.VITE_ENDPOINT);
      url.pathname = '/custom-domain/resolve';
      url.searchParams.set('host', window.location.hostname);
      const res = await fetch(url.toString());
      if (res.status === 404) return false; // authoritative: not a registered domain
      if (!res.ok) throw new Error(`resolve failed: ${res.status}`);
      const data = await res.json();
      const entry: CustomDomainOrg = {
        hostname: window.location.hostname,
        organizationId: data.organizationId,
        slug: data.slug,
        logoUrl: data.logoUrl ?? null,
      };
      localStorage.setItem(CUSTOM_DOMAIN_CACHE_KEY, JSON.stringify(entry));
      return true;
    } catch {
      if (attempt < RESOLVE_ATTEMPTS - 1) await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
    }
  }
  return false;
};
